import React, { useState, useEffect } from 'react';
import { DragDropContext, Draggable, DropResult } from 'react-beautiful-dnd';
import { Card, Badge, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt,
  faEye,
  faPencil,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import { format } from 'date-fns';
import { useMutation, useQueryClient } from 'react-query';
import { UPDATE_TASK_STATUS } from 'services/api/task';
import toast from 'react-hot-toast';
import { KanbanColumn, TaskKanbanProps } from '../types/types';
import StrictModeDroppable from './StrictModeDroppable';
import '../styles/task.css';
import TaskCardSkeleton from './TaskLoader';

const TaskKanban: React.FC<TaskKanbanProps> = ({
  tasks,
  onEdit,
  onDelete,
  onView
}) => {
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState<boolean>(false);
  const [localTasks, setLocalTasks] = useState<any[]>([]);
  const [loadingTaskId, setLoadingTaskId] = useState<string | null>(null);

  useEffect(() => {
    setEnabled(true);
    setLocalTasks(tasks);
  }, [tasks]);

  const statusMapping: { [key: string]: string } = {
    'not-started': 'not started',
    'in-progress': 'in progress',
    completed: 'completed'
  };

  const columns: KanbanColumn[] = [
    {
      id: 'not-started',
      title: 'Not Started',
      tasks: localTasks.filter(task => task.status === 'not started')
    },
    {
      id: 'in-progress',
      title: 'In Progress',
      tasks: localTasks.filter(task => task.status === 'in progress')
    },
    {
      id: 'completed',
      title: 'Completed',
      tasks: localTasks.filter(task => task.status === 'completed')
    }
  ];

  const priorityColors: Record<string, string> = {
    high: 'danger',
    medium: 'warning',
    low: 'success'
  };

  const updateTaskMutation = useMutation(
    (data: { taskId: string; status: string }) =>
      UPDATE_TASK_STATUS({
        taskId: data.taskId,
        status: statusMapping[data.status] || data.status
      }),
    {
      onMutate: async newData => {
        await queryClient.cancelQueries(['tasks']);
        setLoadingTaskId(newData.taskId);

        setLocalTasks(prev =>
          prev.map(task =>
            ((task as any).id || task._id)?.toString() === newData.taskId
              ? {
                  ...task,
                  status: statusMapping[newData.status] || newData.status
                }
              : task
          )
        );
        return { previousTasks: tasks };
      },
      onSuccess: () => {
        queryClient.invalidateQueries(['tasks']);
        setLoadingTaskId(null);
        toast.success('Task status updated');
      },
      onError: (_, __, context: any) => {
        if (context?.previousTasks) {
          setLocalTasks(context.previousTasks);
        }
        setLoadingTaskId(null);
        toast.error('Failed to update task status');
      }
    }
  );

  const onDragEnd = (result: DropResult) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;

    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    if (destination.droppableId !== source.droppableId) {
      const taskId = draggableId;
      const newStatus = destination.droppableId;

      updateTaskMutation.mutate({
        taskId,
        status: newStatus
      });
    }
  };

  if (!localTasks || localTasks.length === 0) {
    return (
      <div className="kanban-board">
        <Row className="g-3">
          {columns.map(column => (
            <Col key={column.id} md={4}>
              <Card className="kanban-column h-100">
                <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">{column.title}</h5>
                  <Badge bg="secondary" pill>
                    0
                  </Badge>
                </Card.Header>
                <div className="p-2 kanban-column-content">
                  <p className="text-center text-muted mt-4">No tasks</p>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  }

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="kanban-board">
        <Row className="g-3">
          {columns.map(column => (
            <Col key={column.id} md={4}>
              <Card className="kanban-column h-100">
                <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">{column.title}</h5>
                  <Badge bg="primary" pill>
                    {column.tasks.length}
                  </Badge>
                </Card.Header>
                {enabled && (
                  <StrictModeDroppable droppableId={column.id} key={column.id}>
                    {provided => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className="p-2 kanban-column-content"
                      >
                        {column.tasks.map((task, index) => (
                          <React.Fragment
                            key={((task as any).id || task._id).toString()}
                          >
                            {loadingTaskId ===
                            ((task as any).id || task._id)?.toString() ? (
                              <Draggable
                                draggableId={(
                                  (task as any).id || task._id
                                ).toString()}
                                index={index}
                                isDragDisabled={true}
                              >
                                {dragProvided => (
                                  <div
                                    ref={dragProvided.innerRef}
                                    {...dragProvided.draggableProps}
                                    {...dragProvided.dragHandleProps}
                                  >
                                    <TaskCardSkeleton />
                                  </div>
                                )}
                              </Draggable>
                            ) : (
                              <Draggable
                                draggableId={(
                                  (task as any).id || task._id
                                ).toString()}
                                index={index}
                              >
                                {dragProvided => (
                                  <Card
                                    ref={dragProvided.innerRef}
                                    {...dragProvided.draggableProps}
                                    {...dragProvided.dragHandleProps}
                                    className="mb-2 task-card"
                                  >
                                    <Card.Body>
                                      <div className="d-flex justify-content-between mb-2">
                                        <Badge
                                          bg={
                                            priorityColors[task.priority] ||
                                            'secondary'
                                          }
                                          pill
                                        >
                                          {task.priority}
                                        </Badge>
                                        <div className="d-flex gap-2">
                                          <span
                                            className="cursor-pointer text-primary"
                                            onClick={() =>
                                              onView(
                                                (task as any).id || task._id
                                              )
                                            }
                                          >
                                            <FontAwesomeIcon icon={faEye} />
                                          </span>
                                          <span
                                            className="cursor-pointer text-primary"
                                            onClick={() => onEdit(task)}
                                          >
                                            <FontAwesomeIcon icon={faPencil} />
                                          </span>
                                          <span
                                            className="cursor-pointer text-danger"
                                            onClick={() =>
                                              onDelete(
                                                (task as any).id || task._id
                                              )
                                            }
                                          >
                                            <FontAwesomeIcon icon={faTrash} />
                                          </span>
                                        </div>
                                      </div>
                                      <h6 className="fw-bold task-title">
                                        {task.task}
                                      </h6>
                                      <p className="small mb-2 text-truncate">
                                        {task.description || 'No description'}
                                      </p>

                                      {/* Job details */}
                                      {(task as any).job && (
                                        <div className="small mb-2 text-muted">
                                          <strong>
                                            {(task as any).job.title}
                                          </strong>
                                          {(task as any).job.well && (
                                            <span>
                                              {' '}
                                              | Well: {(task as any).job.well}
                                            </span>
                                          )}
                                        </div>
                                      )}

                                      <div className="d-flex justify-content-between align-items-center mt-3">
                                        <div className="small text-muted">
                                          <FontAwesomeIcon
                                            icon={faCalendarAlt}
                                            className="me-1"
                                          />
                                          {format(
                                            new Date(task.date),
                                            'MMM dd, yyyy'
                                          )}
                                        </div>
                                        <div>
                                          {task.assignedTo &&
                                            task.assignedTo.length > 0 && (
                                              <div className="assigned-users">
                                                {task.assignedTo
                                                  .slice(0, 2)
                                                  .map(
                                                    (user: any, i: number) => (
                                                      <span
                                                        key={i}
                                                        className="kanbanboard-avatar avatar-initials rounded-circle bg-primary text-white"
                                                        style={{
                                                          marginLeft:
                                                            i > 0 ? '-8px' : '0'
                                                        }}
                                                        title={`${user.firstName} ${user.lastName}`}
                                                      >
                                                        {user.firstName?.charAt(
                                                          0
                                                        )}
                                                        {user.lastName?.charAt(
                                                          0
                                                        )}
                                                      </span>
                                                    )
                                                  )}
                                                {task.assignedTo.length > 2 && (
                                                  <span
                                                    className="kanbanboard-avatar avatar-initials rounded-circle bg-secondary text-white"
                                                    style={{
                                                      marginLeft: '-8px'
                                                    }}
                                                  >
                                                    +
                                                    {task.assignedTo.length - 2}
                                                  </span>
                                                )}
                                              </div>
                                            )}
                                        </div>
                                      </div>
                                    </Card.Body>
                                  </Card>
                                )}
                              </Draggable>
                            )}
                          </React.Fragment>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </StrictModeDroppable>
                )}
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    </DragDropContext>
  );
};

export default TaskKanban;
